import { Theme, Users } from "@just-in-types";
import * as React from "react";
import styled from "react-emotion";
import { connect } from "react-redux";
import Button from "../components/Button";
import Column from "../components/Column";
import Container from "../components/Container";
import Input from "../components/Input";
import Keypad from "../components/Keypad";
import NextButton from "../components/NextButton";
import ResetButton from "../components/ResetButton";
import Row from "../components/Row";
import Title from "../components/Title";
import ErrorBoundary from "../hoc/withErrorBoundary";
import { RouteComponentProps } from "../Site";
import { navigateToNext, NavigateToNext } from "../store/actions";
import { IState } from "../store/reducers";
import { IStrings } from "../store/reducers/strings";

interface INameDispatchProps {
  onNavigateToNext: NavigateToNext;
}
interface INameStateProps {
  languageStrings: IStrings;
  theme: Theme.Props;
  user: Users.User;
}
type NameProps = RouteComponentProps & INameDispatchProps & INameStateProps;
interface INameState {
  firstname: string;
  firstnameInput: null | HTMLElement;
  firstnameIsValid: boolean;
  hasFocus: "firstname" | "lastname";
  isUppercase: boolean;
  lastname: string;
  lastnameIsValid: boolean;
  sending: boolean;
}
type NameState = INameState;

const NameContainer = styled(Container)``;
const NameInput = styled(Input)`
  align-items: center;
  box-sizing: border-box;
  display: flex;
  font-family: monospace;
  font-size: 1.5rem;
  height: 4rem;
  justify-content: center;
  padding-left: 1rem;
  pointer-events: auto;
  text-align: left;
  width: 14rem;
  /* pointer-events: none; */
`;

class Name extends React.Component<NameProps, NameState> {
  public state: NameState = {
    firstname: "",
    firstnameInput: null,
    firstnameIsValid: false,
    hasFocus: "firstname",
    isUppercase: true,
    lastname: "",
    lastnameIsValid: false,
    sending: false,
  };
  private firstnameInput: null | HTMLElement = null;
  private lastnameInput: null | HTMLElement = null;

  public componentDidMount() {
    const {
      onNavigateToNext,
      user: { name },
    } = this.props;

    const search = window.location.search;
    const params = new URLSearchParams(search);
    const from = params.get("from");

    if (name.displayName && from !== "thanks") {
      onNavigateToNext();
    }

    this.firstnameInput = document.getElementById("name-input--firstname");
    this.lastnameInput = document.getElementById("name-input--lastname");

  }

  public handleKeypadButton = (event: React.MouseEvent<HTMLButtonElement>) => {
    const { firstname, hasFocus, lastname } = this.state;
    const { value } = event.currentTarget;

    const name = this.keypadValidator(hasFocus === "firstname" ? firstname : lastname, hasFocus, value);

    if (hasFocus === "firstname") {
      this.setState({ firstname: name });
    }

    if (hasFocus === "lastname") {
      this.setState({ lastname: name });
    }
  };

  public focusFirstname = () => {
    if (this.firstnameInput) {
      this.firstnameInput.focus();
    }
  };

  public focusLastname = () => {
    if (this.lastnameInput) {
      this.lastnameInput.focus();
    }
  };

  public render() {
    const {
      languageStrings: strings,
      routes: {
        name: { optional },
      },
      user: { name, userId },
      site: { disableKeyboard },
      theme: { colors },
      site: { kioskUiBackgroundColor, kioskUiTitleTextColor, kioskUiButtonErrorColor, kioskUiButtonSuccessColor },
      site,
    } = this.props;

    const { firstname, firstnameIsValid, hasFocus, isUppercase, lastname, lastnameIsValid, sending } = this.state;
    const firstnamePlaceholder = strings.firstname;
    const lastnamePlaceholder = strings.lastname;
    const showOptionalNext = userId !== "opt-out" && optional;

    return (
      <ErrorBoundary>
        <NameContainer style={{ background: kioskUiBackgroundColor }}>
          <Row align="center" justify="center">
            <Title color={kioskUiTitleTextColor ? kioskUiTitleTextColor : colors.secondary}>{strings.nameHeader}</Title>
          </Row>
          <Column align="center" justify="space-around">
            <Row>
              <NameInput
                focused={hasFocus === "firstname"}
                id="name-input--firstname"
                name="firstname"
                onChange={this.handleInput}
                onFocus={this.handleFocus}
                placeholder={firstnamePlaceholder}
                readOnly={disableKeyboard}
                type="text"
                valid={firstnameIsValid}
                value={firstname}
                autoComplete="off"
              />
              <NameInput
                focused={hasFocus === "lastname"}
                id="name-input--lastname"
                name="lastname"
                onChange={this.handleInput}
                onClick={this.handleFocus}
                onFocus={this.handleFocus}
                placeholder={lastnamePlaceholder}
                readOnly={disableKeyboard}
                type="text"
                valid={lastnameIsValid}
                value={lastname}
                autoComplete="off"
              />
            </Row>
            <Keypad
              colors={colors}
              disabled={sending}
              isUppercase={isUppercase}
              onFocusNextInput={firstnameIsValid && hasFocus === "firstname" ? this.focusLastname : undefined}
              onFocusPreviousInput={hasFocus === "lastname" ? this.focusFirstname : undefined}
              onHandleKeypadClick={this.handleKeypadButton}
              type="letters"
              site={site}
            />
          </Column>
          <Row align="center" justify="center">
            <ResetButton site={site} />
            {showOptionalNext && (
              <NextButton
                bg={kioskUiButtonErrorColor ? kioskUiButtonErrorColor : colors.error}
                disabled={false}
                label={"No Thanks"}
                name="name"
                value={{
                  displayName: firstname && lastname ? `${firstname} ${lastname}` : null,
                  firstname,
                  lastname,
                }}
              />
            )}
            <NextButton
              bg={kioskUiButtonSuccessColor ? kioskUiButtonSuccessColor : colors.success}
              disabled={!(firstnameIsValid && lastnameIsValid)}
              name="name"
              value={{
                displayName: `${firstname} ${lastname}`,
                firstname,
                lastname,
              }}
            >
              {strings.addName}
            </NextButton>
          </Row>
        </NameContainer>
      </ErrorBoundary>
    );
  }

  private handleInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    let isValid = false;
    const { name, value } = event.currentTarget;

    const finalValue = value.charAt(0).toUpperCase() + value.slice(1);

    if (name === "firstname") {
      this.setState({ firstname: finalValue });
    }

    if (name === "lastname") {
      this.setState({ lastname: finalValue });
    }

    if (finalValue.length > 0) {
      isValid = true;
    }

    // console.log('finalValue', finalValue.length);

    if (name === "firstname") {
      this.setState({ firstnameIsValid: isValid });
    }

    if (name === "lastname") {
      this.setState({ lastnameIsValid: isValid });
    }
  };

  private handleFocus = (event: React.FocusEvent<HTMLInputElement> | React.MouseEvent<HTMLInputElement>) => {
    const { firstname, lastname } = this.state;
    const { name } = event.currentTarget;

    if ((name === "firstname" && firstname.length === 0) || (name === "lastname" && lastname.length === 0)) {
      this.setState({ isUppercase: true });
    }

    if (name === "firstname" || name === "lastname") {
      this.setState({ hasFocus: name });
    }
  };

  private keypadValidator = (name: string, type: "firstname" | "lastname", value: string) => {
    let isValid = false;
    let newName = "";
    let newValue = value;

    if (value === "clear") {
      isValid = false;
      newName = "";
    } else if (value === "backspace") {
      newName = name.slice(0, -1);
    } else {
      if (name.length === 0) {
        newValue = value.toUpperCase();

        this.setState({ isUppercase: false });
      }

      newName = name.concat(newValue);
    }

    if (newName.length > 0) {
      isValid = true;
    }

    if (type === "firstname") {
      this.setState({ firstnameIsValid: isValid });
    }

    if (type === "lastname") {
      this.setState({ lastnameIsValid: isValid });
    }

    return newName;
  };
}

const mapStateToProps = ({ router: { languageStrings }, theme, user }: IState) => ({
  languageStrings,
  theme,
  user,
});

const mapDispatchToProps = {
  onNavigateToNext: navigateToNext,
};

export default connect<INameStateProps, INameDispatchProps>(mapStateToProps, mapDispatchToProps)(Name);



// WEBPACK FOOTER //
// ./src/views/Name.tsx